/** Area graph: deterministic layered layout from the join point; exits as edges with their state. */
export interface MapArea {
	id: string;
	name: string;
	joinPoint?: boolean;
}
export interface MapExit {
	id: string;
	from: string;
	to: string;
	name: string;
	state: string;
	oneWay?: boolean;
	lock?: string;
}

export function layout(areas: MapArea[], exits: MapExit[]): Record<string, { x: number; y: number }> {
	const start = areas.find((a) => a.joinPoint)?.id ?? areas[0]?.id;
	const depth: Record<string, number> = {};
	if (start) depth[start] = 0;
	const queue = start ? [start] : [];
	while (queue.length) {
		const cur = queue.shift()!;
		for (const x of exits) {
			const next = x.from === cur ? x.to : x.to === cur ? x.from : null;
			if (next && depth[next] === undefined) {
				depth[next] = depth[cur] + 1;
				queue.push(next);
			}
		}
	}
	let maxDepth = Math.max(0, ...Object.values(depth));
	for (const a of areas) if (depth[a.id] === undefined) depth[a.id] = ++maxDepth;
	const cols: Record<number, string[]> = {};
	for (const a of areas) (cols[depth[a.id]] ??= []).push(a.id);
	const pos: Record<string, { x: number; y: number }> = {};
	for (const [d, ids] of Object.entries(cols)) ids.forEach((id, i) => (pos[id] = { x: 90 + Number(d) * 190, y: 60 + i * 110 + (Number(d) % 2) * 30 }));
	return pos;
}

export function MapView(p: {
	areas: MapArea[];
	exits: MapExit[];
	here?: string;
	tokens?: Record<string, string[]>;
	selected?: string | null;
	onSelect?: (id: string) => void;
	onSelectExit?: (id: string) => void;
	compact?: boolean;
}) {
	const pos = layout(p.areas, p.exits);
	const w = Math.max(320, ...Object.values(pos).map((v) => v.x + 110));
	const h = Math.max(160, ...Object.values(pos).map((v) => v.y + 70));
	const stroke = (s: string) => (s === 'locked' ? 'var(--warn)' : s === 'hidden' ? 'var(--muted)' : s === 'removed' ? 'var(--bad)' : 'var(--line-strong)');
	return (
		<svg class={`map ${p.compact ? 'map-compact' : ''}`} viewBox={`0 0 ${w} ${h}`} role="img" aria-label="Area map">
			{p.exits.map((x) => {
				const a = pos[x.from];
				const b = pos[x.to];
				if (!a || !b) return null;
				return (
					<g key={x.id} class="map-edge" onClick={() => p.onSelectExit?.(x.id)}>
						<line x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke={stroke(x.state)} stroke-width={2} stroke-dasharray={x.state === 'hidden' || x.state === 'removed' ? '5 5' : undefined} marker-end={x.oneWay ? 'url(#arrow)' : undefined} />
					</g>
				);
			})}
			<defs>
				<marker id="arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
					<path d="M 0 0 L 10 5 L 0 10 z" fill="var(--line-strong)" />
				</marker>
			</defs>
			{p.areas.map((a) => {
				const v = pos[a.id];
				const toks = p.tokens?.[a.id] ?? [];
				return (
					<g key={a.id} class={`map-node ${p.here === a.id ? 'map-here' : ''} ${p.selected === a.id ? 'map-sel' : ''}`} onClick={() => p.onSelect?.(a.id)} transform={`translate(${v.x - 75}, ${v.y - 24})`}>
						<rect width={150} height={48} rx={8} />
						<text x={75} y={20} class="map-node-name">
							{a.name.length > 22 ? `${a.name.slice(0, 21)}…` : a.name}
						</text>
						{toks.length ? (
							<text x={75} y={38} class="map-node-tokens">
								{toks.slice(0, 3).join(', ')}
								{toks.length > 3 ? ` +${toks.length - 3}` : ''}
							</text>
						) : null}
					</g>
				);
			})}
			{!p.compact
				? p.exits.map((x) => {
						const a = pos[x.from];
						const b = pos[x.to];
						if (!a || !b) return null;
						return (
							<text key={`l-${x.id}`} x={(a.x + b.x) / 2} y={(a.y + b.y) / 2 + 4} class="map-edge-label" onClick={() => p.onSelectExit?.(x.id)}>
								{x.name}
								{x.state !== 'open' ? ` (${x.state})` : ''}
								{x.lock ? ' 🔑' : ''}
							</text>
						);
					})
				: null}
		</svg>
	);
}
